import React, {
	createContext,
	FC,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';

import { getFromLocalStorage, saveToLocalStorage } from '../../api/LocalStorageApi';
import { ICollection, ICollectionCreate, ICollectionUpdate } from '../../interfaces/Collection';
import {
	apiCreateCollection,
	apiEditCollection,
	apiGetCollections,
	apiRemoveCollection
} from '../../services/CollectionsService';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { useConfiguration } from '../configurationProvider/ConfigurationProvider';
import { useNotifications } from '../notificationsProvider/NotificationsConsumer';

import { handleRequest } from './CollectionsProviderHelper';

interface ICollectionsContext {
	propertyCollections: ICollection[];
	showOnlyPropertiesInMyCollection: boolean;
	isCollectionsLoading: boolean;
	isCollectionUpdating: boolean;
	updatePropertiesCollectionState: (showOnlyPropertiesInMyCollection: boolean) => void;
	createCollection: (collection: ICollectionCreate, onClose: () => void) => Promise<void>;
	editCollection: (collection: ICollectionUpdate, onClose: () => void) => Promise<void>;
	removeCollection: (collection: ICollection) => Promise<void>;
	myCollection?: ICollection;
}

export const MY_COLLECTION_NAME = 'My collection';
const CollectionsContext = createContext<ICollectionsContext>(null);

const CollectionsProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
	const { getToggle } = useConfiguration();
	const notificationsContext = useNotifications();
	const isMultipleCollectionsEnabled = getToggle(SiteScannerUiToggles.ENABLE_MULTIPLE_COLLECTIONS);

	const [propertyCollections, setPropertyCollections] = useState<ICollection[]>([]);
	// condition should be removed when multiple collections feature is fully integrated
	const [showOnlyPropertiesInMyCollection, setShowOnlyPropertiesInMyCollection] = useState(
		isMultipleCollectionsEnabled
			? false
			: JSON.parse(getFromLocalStorage(MY_COLLECTION_NAME)) || false
	);
	const [isCollectionsLoading, setIsCollectionsLoading] = useState(true);
	const [isCollectionUpdating, setIsCollectionUpdating] = useState(false);

	// should be removed when multiple collections feature is fully integrated
	const myCollection = useMemo((): ICollection => {
		return (
			propertyCollections.find(({ name }) => name === MY_COLLECTION_NAME) || propertyCollections[0]
		);
	}, [propertyCollections]);

	// should be removed when multiple collections feature is fully integrated
	const updatePropertiesCollectionState = useMemo(() => {
		return (showOnlyPropertiesInMyCollection: boolean): void => {
			saveToLocalStorage(MY_COLLECTION_NAME, showOnlyPropertiesInMyCollection);
			setShowOnlyPropertiesInMyCollection(showOnlyPropertiesInMyCollection);
		};
	}, [setShowOnlyPropertiesInMyCollection]);

	const getCollections = useCallback(async (): Promise<void> => {
		setIsCollectionsLoading(true);
		await handleRequest(
			async () => {
				const collections: ICollection[] = await apiGetCollections();

				setPropertyCollections(collections);
			},
			{ failedText: 'Failed to fetch collections', notificationsContext }
		);
		setIsCollectionsLoading(false);
	}, [notificationsContext]);

	const createCollection = useCallback(
		async (collection: ICollection, onClose: () => void): Promise<void> => {
			setIsCollectionUpdating(true);
			await handleRequest(
				async () => {
					await apiCreateCollection(collection);
					await getCollections();
				},
				{
					successText: 'Collection created',
					failedText: 'Failed to create the collection',
					onClose,
					notificationsContext
				}
			);
			setIsCollectionUpdating(false);
		},
		[getCollections, notificationsContext]
	);

	const editCollection = useCallback(
		async (collection: ICollection, onClose: () => void): Promise<void> => {
			setIsCollectionUpdating(true);
			await handleRequest(
				async () => {
					await apiEditCollection(collection);
					await getCollections();
				},
				{
					successText: 'Collection saved',
					failedText: 'Failed to save the collection',
					onClose,
					notificationsContext
				}
			);
			setIsCollectionUpdating(false);
		},
		[getCollections, notificationsContext]
	);

	const removeCollection = useCallback(
		async (collection: ICollection): Promise<void> => {
			setIsCollectionUpdating(true);
			await handleRequest(
				async () => {
					await apiRemoveCollection(collection.id);
					await getCollections();
				},
				{
					successText: 'Collection deleted',
					failedText: 'Failed to delete the collection',
					notificationsContext
				}
			);
			setIsCollectionUpdating(false);
		},
		[getCollections, notificationsContext]
	);

	useEffect(() => {
		getCollections();
	}, [getCollections]);

	const value = useMemo(() => {
		return {
			propertyCollections,
			showOnlyPropertiesInMyCollection,
			isCollectionsLoading,
			isCollectionUpdating,
			updatePropertiesCollectionState,
			createCollection,
			editCollection,
			removeCollection,
			myCollection
		};
	}, [
		propertyCollections,
		showOnlyPropertiesInMyCollection,
		isCollectionsLoading,
		isCollectionUpdating,
		updatePropertiesCollectionState,
		createCollection,
		editCollection,
		removeCollection,
		myCollection
	]);

	return <CollectionsContext.Provider value={value}>{children}</CollectionsContext.Provider>;
};

const useCollections = (): ICollectionsContext => useContext(CollectionsContext);

export { CollectionsProvider, useCollections };
